import React, { useRef, useLayoutEffect } from "react";

// ---------------------------------------------------------------------------
// Word-level diff (LCS over tokens)
// ---------------------------------------------------------------------------
// NOTE: This diff is used by the template comparison view (TemplateCompareModal).
// The add-version editor below does NOT use it - it tracks the characters you
// actually type instead (see the edit-tracking section).

// Split into alternating runs of whitespace / non-whitespace so spacing and
// newlines are preserved and changes land at word granularity.
const tokenizeWords = (text) => {
  const out = [];
  if (!text) return out;
  const re = /(\s+|\S+)/g;
  let m;
  while ((m = re.exec(text)) !== null) out.push(m[0]);
  return out;
};

// Coarser fallback for very large inputs: tokens are lines plus their newlines.
const tokenizeLines = (text) => {
  if (!text) return [];
  return text.split(/(\n)/).filter((part) => part !== "");
};

// Diff two token arrays, returning merged runs:
// [{ type: 'added' | 'removed' | 'unchanged', text }]
const diffTokens = (a, b) => {
  const n = a.length;
  const m = b.length;
  const w = m + 1;
  const lcs = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      if (a[i] === b[j]) {
        lcs[i * w + j] = lcs[(i + 1) * w + (j + 1)] + 1;
      } else {
        lcs[i * w + j] = Math.max(lcs[(i + 1) * w + j], lcs[i * w + (j + 1)]);
      }
    }
  }

  const out = [];
  const push = (type, text) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ type, text });
  };

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push("unchanged", b[j]);
      i += 1;
      j += 1;
    } else if (lcs[(i + 1) * w + j] >= lcs[i * w + (j + 1)]) {
      push("removed", a[i]);
      i += 1;
    } else {
      push("added", b[j]);
      j += 1;
    }
  }
  while (i < n) {
    push("removed", a[i]);
    i += 1;
  }
  while (j < m) {
    push("added", b[j]);
    j += 1;
  }
  return out;
};

// Cap the O(n*m) work so huge templates stay responsive; fall back to a
// line-level diff when the word-token product gets too large.
const MAX_TOKEN_PRODUCT = 1500000;

export const computeWordDiff = (baseText, currText) => {
  let a = tokenizeWords(baseText);
  let b = tokenizeWords(currText);
  if (a.length * b.length > MAX_TOKEN_PRODUCT) {
    a = tokenizeLines(baseText);
    b = tokenizeLines(currText);
  }
  return diffTokens(a, b);
};

// ---------------------------------------------------------------------------
// Edit tracking: mark exactly the characters the user types as "added"
// ---------------------------------------------------------------------------

// Update the per-character "added" mask for a single edit, using the caret so
// we know precisely which characters were inserted (a content diff can't tell a
// typed word from an identical pre-existing one). Falls back to a common
// prefix/suffix split for selection-replacements.
const reconcileMask = (oldText, oldMask, newText, caret) => {
  const oldLen = oldText.length;
  const newLen = newText.length;
  const delta = newLen - oldLen;

  // Pure insertion of `delta` chars ending at the caret.
  if (delta > 0) {
    const start = caret - delta;
    if (
      start >= 0 &&
      newText.slice(0, start) === oldText.slice(0, start) &&
      newText.slice(caret) === oldText.slice(start)
    ) {
      return [
        ...oldMask.slice(0, start),
        ...new Array(delta).fill(true),
        ...oldMask.slice(start),
      ];
    }
  }

  // Pure deletion of `-delta` chars, with the caret left at the deletion point.
  if (delta < 0) {
    const removed = -delta;
    if (
      newText.slice(0, caret) === oldText.slice(0, caret) &&
      newText.slice(caret) === oldText.slice(caret + removed)
    ) {
      return [...oldMask.slice(0, caret), ...oldMask.slice(caret + removed)];
    }
  }

  // Fallback (selection replaced, multi-caret, etc.): common prefix/suffix,
  // mark the changed middle as added.
  let p = 0;
  const maxP = Math.min(oldLen, newLen);
  while (p < maxP && oldText[p] === newText[p]) p += 1;
  let s = 0;
  const maxS = Math.min(oldLen - p, newLen - p);
  while (s < maxS && oldText[oldLen - 1 - s] === newText[newLen - 1 - s]) s += 1;
  const inserted = newLen - p - s;
  return [
    ...oldMask.slice(0, p),
    ...new Array(inserted).fill(true),
    ...oldMask.slice(oldLen - s),
  ];
};

// Merge a text + mask into runs of { added, text } for rendering.
const buildSegments = (text, mask) => {
  const segments = [];
  for (let i = 0; i < text.length; i += 1) {
    const added = !!mask[i];
    const last = segments[segments.length - 1];
    if (last && last.added === added) last.text += text[i];
    else segments.push({ added, text: text[i] });
  }
  return segments;
};

const MAX_CACHE = 50;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Shared inner text styling so the editable textarea and the highlight backdrop
// wrap identically (same font, padding, wrapping).
const TEXT_CLASS =
  "w-full px-3 py-2 font-mono text-sm leading-relaxed whitespace-pre-wrap break-words";

// An "add version" editor that highlights the text you type (vs. the starting
// baseText) in green. A transparent <textarea> handles all editing (caret,
// selection, paste, undo, IME); a backdrop behind it paints the same text with
// the typed characters in green. The highlight comes from tracking the actual
// edits (not a diff), so a word typed next to an identical existing word is
// highlighted correctly. Only plain text is ever emitted, so a saved template
// has no colour.
const TrackChangesEditor = ({
  baseText = "",
  value = "",
  onChange,
  placeholder = "",
  className = "",
}) => {
  const textareaRef = useRef(null);

  // Editing state kept in a ref so it survives renders without causing extra
  // ones. `cache` maps a text string to the mask it had, so undo/redo restore
  // the exact highlighting for that text.
  const stateRef = useRef({ base: null, text: "", mask: [], cache: new Map() });
  const s = stateRef.current;

  // Reset when the dialog opens for a new template/version (baseText changes),
  // or when value changes from something other than our own onChange (prefill).
  if (s.base !== baseText) {
    s.base = baseText;
    s.text = value;
    s.mask = new Array(value.length).fill(false);
    s.cache = new Map();
  } else if (s.text !== value) {
    s.text = value;
    s.mask = new Array(value.length).fill(false);
    s.cache = new Map();
  }

  const segments = buildSegments(value, s.mask);

  const handleChange = (e) => {
    const newText = e.target.value;
    const caret = e.target.selectionEnd ?? newText.length;

    if (s.cache.has(newText)) {
      // Returned to a previously seen text (undo/redo/retype): reuse its mask.
      s.mask = s.cache.get(newText);
    } else {
      const newMask = reconcileMask(s.text, s.mask, newText, caret);
      s.cache.set(s.text, s.mask);
      s.cache.set(newText, newMask);
      if (s.cache.size > MAX_CACHE) {
        // Drop oldest entries (Map preserves insertion order).
        const drop = s.cache.size - MAX_CACHE;
        const keys = s.cache.keys();
        for (let k = 0; k < drop; k += 1) s.cache.delete(keys.next().value);
      }
      s.mask = newMask;
    }
    s.text = newText;
    onChange?.(newText);
  };

  // Grow the textarea to fit its content (measured before paint) so it never
  // scrolls on its own; the fixed-height container below scrolls both layers
  // together with no JS sync.
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [value]);

  return (
    <div
      className={`relative h-[340px] min-h-[240px] resize-y overflow-auto rounded-md border border-gray-300 bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${className}`}
    >
      {/* Inner wrapper both layers share, so they wrap to one width (unaffected
          by the scrollbar) and scroll together as a single unit. */}
      <div className="relative min-h-full">
        {/* Backdrop: same text as the textarea, typed characters in green. */}
        <div
          aria-hidden="true"
          className={`${TEXT_CLASS} absolute inset-0 text-gray-900 pointer-events-none`}
        >
          {value ? (
            segments.map((seg, i) =>
              seg.added ? (
                <span key={i} className="bg-green-200 text-green-800 rounded-sm">
                  {seg.text}
                </span>
              ) : (
                <span key={i}>{seg.text}</span>
              )
            )
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
          {/* Keep heights matched when the value ends on a blank line. */}
          {value.endsWith("\n") ? "\u00A0" : null}
        </div>

        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          spellCheck={false}
          className={`${TEXT_CLASS} relative block w-full min-h-[240px] resize-none overflow-hidden border-0 bg-transparent text-transparent caret-black focus:outline-none focus:ring-0`}
        />
      </div>
    </div>
  );
};

export default TrackChangesEditor;
